import { useEffect, useState } from 'react';

import { db } from 'src/utils/firebase';

// Goals are stored in the users profile

/**
 * Custom hook for managing goals in Firestore.
 * @param {string} userId - The user ID to fetch goals for.
 * @returns {object} An object containing goals array and CRUD operations.
 */
const useGoalService = (userId) => {
  const [goals, setGoals] = useState({});
  const userRef = db.collection('users').doc(userId);

  // Effect to subscribe to changes in the goals collection
  useEffect(() => {
    // Check if the user is authenticated before querying for accounts
    if (!userId) {
      setGoals([]); // Set accounts to an empty array when the user is not authenticated
      return () => {}; // Exit the useEffect early
    }

    // Subscribe to changes in the goals collection for the current user
    const unsubscribe = userRef.onSnapshot((snapshot) => {
      // Extract goal data from snapshot and update state
      setGoals(snapshot?.data()?.goals ?? {});
    });

    // Unsubscribe from snapshot listener when component unmounts
    return () => unsubscribe();
  }, [userId]); // Re-run effect when userId changes

  const addGoal = (category, value, date) => persistGoalsToDB({ ...goals, [category]: value });

  async function persistGoalsToDB(newGoals) {
    const userSnapshot = await userRef.get();
    if (userSnapshot.exists) {
      // Goal exists, update it
      await userRef.update({ goals: newGoals }, { merge: true });
    } else {
      // Goal doesn't exist, create it
      await userRef.set({ goals: newGoals });
    }
  }

  // Return goals array and CRUD operations
  return { goals, addGoal, save: persistGoalsToDB };
};

export default useGoalService;
